// src/components/UploadProgress.tsx (Adapted)
import { CheckIcon } from "@radix-ui/react-icons"; // Assuming Cross for errors
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../store/store"; // Adjust path
import {
  UploadStatus,
  UploadStatuses,
  cancelUpload,
  clearUploadJob,
} from "../store/slices/uploadFileSlice"; // Adjust path
import Button from "./shared/Button";

const SpinningWheel = () => (
  <div className="inline-block h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
);

// Helper to get a user-friendly status message
const getStatusText = (status: UploadStatus | null): string => {
  if (!status) return "Waiting...";
  switch (status) {
    case UploadStatuses.PENDING_UPLOAD:
      return "Pending Upload...";
    case UploadStatuses.UPLOADING:
      return "Uploading...";
    case UploadStatuses.UPLOADED:
      return "Upload Complete, Processing...";
    case UploadStatuses.PROCESSING_STARTED:
      return "Processing Started...";
    case UploadStatuses.PROCESSING_CPP:
      return "Processing Data..."; // Example
    case UploadStatuses.STORING_S3:
      return "Storing Original File...";
    case UploadStatuses.COMPLETED:
      return "Completed";
    case UploadStatuses.FAILED_TRANSFER:
      return "Transfer Failed";
    case UploadStatuses.FAILED_PROCESSING:
      return "Processing Failed";
    case UploadStatuses.FAILED_CPP:
      return "Processing Failed (C++)";
    case UploadStatuses.FAILED_S3:
      return "Storage Failed";
    default:
      return status; // Show raw status if unknown
  }
};

export default function UploadProgress() {
  const dispatch = useDispatch<AppDispatch>();
  // Select the whole jobs map
  const uploadJobs = useSelector(
    (state: RootState) => state.uploadFileReducer.jobs,
  );

  // Show any active job, OR any job that was loaded from the server on page refresh
  // (including recently completed/failed — so the user sees the result after a reload).
  const activeJob = Object.values(uploadJobs).find(
    (job) =>
      job.fromHydration ||
      (job.status !== UploadStatuses.COMPLETED &&
        !job.status?.startsWith("FAILED")),
  );
  if (!activeJob) {
    return null; // Hide component if no active uploads/processing
  }

  const {
    uploadId,
    fileName,
    transferProgress,
    status,
    statusDetails,
    error,
    isInterrupted,
  } = activeJob;

  // isInterrupted: UPLOADING status from DB hydration — TUS client is NOT active
  const isUploadingStatus = status === UploadStatuses.UPLOADING;
  const isInterruptedTransfer = isUploadingStatus && !!isInterrupted;
  const isActivelyTransferring = isUploadingStatus && !isInterrupted;
  const isTransferring = isUploadingStatus; // used for Cancel button visibility
  const isProcessing =
    status &&
    !isUploadingStatus &&
    status !== UploadStatuses.COMPLETED &&
    !status.startsWith("FAILED");
  const isComplete = status === UploadStatuses.COMPLETED;
  const isFailed = status?.startsWith("FAILED");

  const displayProgress = isActivelyTransferring
    ? transferProgress
    : isComplete
      ? 100
      : 0;

  return (
    <div className="fixed bottom-4 right-4 bg-neutral-800 text-neutral-300 p-4 rounded-lg shadow-lg w-72 z-50">
      {/* File Name */}
      <div className="mb-2 flex justify-between items-center gap-2">
        <span className="text-sm font-medium truncate block">
          {fileName || uploadId}
        </span>
        <div className="flex items-center gap-1">
          {/* Cancel only while TUS client is actively transferring */}
          {isTransferring && !isInterruptedTransfer && (
            <Button
              variant="dangerDiscrete"
              size="sm"
              onClick={() => dispatch(cancelUpload(uploadId))}
              title="Cancel upload"
            >
              Cancel
            </Button>
          )}
          {/* Dismiss for interrupted transfers, completed, or failed jobs */}
          {(isInterruptedTransfer || isComplete || isFailed) && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => dispatch(clearUploadJob({ uploadId }))}
              title="Dismiss"
            >
              &times;
            </Button>
          )}
        </div>
      </div>

      {/* Overall Status Text */}
      <p
        className={`text-sm mb-2 ${isComplete ? "text-green-500" : ""} ${isFailed ? "text-red-500" : ""}`}
      >
        Status:{" "}
        {isInterruptedTransfer ? "Transfer Interrupted" : getStatusText(status)}
      </p>

      {/* Interrupted transfer message — shown instead of progress bar */}
      {isInterruptedTransfer && (
        <div className="text-xs text-amber-400 mb-2">
          {statusDetails || "Re-select the same file to resume the transfer."}
        </div>
      )}

      {/* Active transfer progress bar */}
      {(isActivelyTransferring || isComplete) && (
        <div className="flex items-center gap-2 mb-2">
          {isComplete ? (
            <CheckIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
          ) : (
            <SpinningWheel />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex justify-between text-xs mb-1">
              <span>{isComplete ? "Transferred" : "Transferring"}</span>
              <span>{displayProgress}%</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-1.5">
              <div
                className={`${isComplete ? "bg-green-500" : "bg-blue-600"} h-1.5 rounded-full transition-[width] duration-300`}
                style={{ width: `${displayProgress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Processing spinner */}
      {isProcessing && (
        <div className="flex items-center gap-2 mb-2 text-xs text-neutral-400">
          <SpinningWheel />
          <span>{statusDetails || getStatusText(status)}</span>
        </div>
      )}

      {/* Error details */}
      {isFailed && (
        <p className="text-xs text-red-500 mt-1">
          {statusDetails || error || "An unknown error occurred."}
        </p>
      )}
    </div>
  );
}
